import styled, {css, keyframes} from "styled-components";
import {fontFamilies, motion, overlay, radii} from "theme/tokens";
import {CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX} from "home/landing/campaignSpace";

/**
 * Below this width a tag placed beside the card has no room beside it and opens OVER the card instead
 * (ui#2949). The same breakpoint the landing stacks its campaign under the card at, because that is
 * where the space to the card's left stops existing.
 */
export const PEEL_COMPACT_QUERY = `(max-width: ${CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX - 1}px)`;
const PEEL_WIDE_QUERY = `(min-width: ${CAMPAIGN_SIDE_BY_SIDE_MIN_WIDTH_PX}px)`;
/** How far below the card's top edge a beside tag and its tab hang: level with the link row. */
const BESIDE_TOP_PX = 64;
/** The gap between a beside tag's paper and the card's left edge on a wide layout. */
const BESIDE_GAP_PX = 14;
/** The widths a tag is drawn at: the coupon's ticket, and the gift tag's paper. */
const TICKET_WIDTH = "350px";
const GIFT_TAG_WIDTH = "236px";

export const giftSurface = css`
	background: linear-gradient(160deg, ${({theme}) => theme.tokens.gold}, ${({theme}) => theme.tokens.gold} 55%, ${({theme}) => theme.tokens.goldDark});
	color: ${({theme}) => theme.tokens.goldInk};
	border: 1px solid ${({theme}) => theme.tokens.goldEdge};
	box-shadow: ${({theme}) => theme.tokens.shadowCard};
`;

const cardLift = keyframes`
	0%, 100% { transform: translateY(0) rotate(0); }
	35% { transform: translateY(-8px) rotate(-.8deg); }
`;
const tagPendulum = keyframes`
	0% { transform: translateY(calc(-100% - 26px)) rotate(0); }
	20% { transform: translateY(0) rotate(14deg); }
	40% { transform: translateY(0) rotate(-10deg); }
	60% { transform: translateY(0) rotate(6deg); }
	80% { transform: translateY(0) rotate(-3deg); }
	100% { transform: translateY(0) rotate(0); }
`;
/* Tucking plays the drop backwards (owner 2026-09-09): the tag rides its string back up behind the
   card; no swing on the way up, the card settles afterwards. `both` keeps the final frame until the
   slot unmounts the tag, so nothing flashes back into place. */
/* Faster than the row it sits in closes (a decelerating ease-in on the row, ease-out here): the tag
   is fully behind the card by ~70% of the retreat, so the last frames never show it vanishing
   below the card's edge. The row keeps closing after it; the pull drops once both are done. */
const tagRetreat = keyframes`
	0% { transform: translateY(0) rotate(0); }
	70%, 100% { transform: translateY(calc(-100% - 40px)) rotate(0); }
`;
export const TAG_RETREAT_MS = 620;

/*
 * The dim under a ticket open over a compact card (owner 2026-09-27, ui#2949): the dialogs' own scrim
 * colour, laid over the CARD only, not the screen. The ticket is not modal: nothing traps focus, the
 * page still scrolls and the nav still works, and a full-screen wash would promise a modal it is not.
 * Between the card (2) and the open ticket (3); fades in on the dialogs' entrance timing.
 */
const scrimFade = keyframes`
	0% { opacity: 0; }
	100% { opacity: 1; }
`;
export const Scrim = styled.div`
	position: absolute; inset: 0; z-index: 2;
	border-radius: ${radii.card};
	background: ${overlay.scrim};
	cursor: pointer;
	animation: ${scrimFade} ${motion.enter} ${motion.easeOut} both;
	@media (prefers-reduced-motion: reduce) { animation: none; }
`;
/* Beside the card the tag slides out from behind it and back (ui#2949); over a compact card it rises
   into place and fades back into its tab. Same length as the hanging retreat, so the slot's tuck
   timing holds for both. */
const besideSlideOut = keyframes`
	0% { transform: translateX(calc(100% + ${BESIDE_GAP_PX}px)); }
	100% { transform: translateX(0); }
`;
const besideSlideBack = keyframes`
	0% { transform: translateX(0); }
	70%, 100% { transform: translateX(calc(100% + ${BESIDE_GAP_PX}px)); }
`;
const overlayRise = keyframes`
	0% { opacity: 0; transform: translateY(8px) scale(.98); }
	100% { opacity: 1; transform: none; }
`;
const overlayFade = keyframes`
	0% { opacity: 1; transform: none; }
	70%, 100% { opacity: 0; transform: translateY(8px) scale(.98); }
`;

export const Frame = styled.div<{$pinned: boolean; $entrance: boolean}>`
	position: relative;
	--peel-pin-width: 132px;
	/* The card's own measure, not the column's: between 405px and 900px the sender column fills the
	   viewport while the card keeps its fixed width, and a full-width frame hung the ticket centred on
	   the page rather than under the card (owner 2026-09-09, at 730px). The fixed-width card still
	   sets the frame's width; a fluid card fills it as before. */
	width: fit-content;
	max-width: 100%;
	margin-inline: auto;
	min-width: 0;
	max-height: 100%;
	min-height: 0;
	display: flex;
	flex-direction: column;
	isolation: isolate;
	[data-peel-card] { position: relative; z-index: 2; min-height: 0; display: flex; flex-direction: column; }
	/* A tag may ask the card to LIFT 4px while it slides out (the download-limit card, ui#2468). On
	   top, never a transform: a transformed wrapper becomes the containing block for every
	   position: fixed sheet inside the card and would trap them mid-slide. */
	> [data-peel-card] { transition: top .45s cubic-bezier(.2,.9,.3,1); }
	&:has([data-peel-card-lift]) > [data-peel-card] { top: -4px; }
	> [data-peel-row], > [data-peel-position], > [data-peel-pulls] { flex: none; }
	&[data-peel-pinned] {
		perspective: 900px;
		@media (max-width: 720px) { > [data-peel-card] { order: 1; } }
	}
	${({$entrance, $pinned}) => $entrance && !$pinned && css`
		> [data-peel-card] { animation: ${cardLift} .9s cubic-bezier(.2,.9,.3,1) both; }
	`}
	/* A beside row leaves the flow (ui#2949): the card keeps its height and nothing under it moves. Wide,
	   it hangs from the card's left edge, BEHIND the card so the tag slides out from under it; the tab
	   touches the edge and the paper keeps a gap. Compact, the tab straddles the edge and the paper
	   opens over the card, above it. */
	> [data-peel-beside] { position: absolute; z-index: 1; top: ${BESIDE_TOP_PX}px; right: 100%; }
	> [data-peel-beside="tag"] { margin-right: ${BESIDE_GAP_PX}px; }
	@media ${PEEL_COMPACT_QUERY} {
		> [data-peel-beside] { z-index: 3; }
		> [data-peel-beside="tag"] { top: 12px; left: 12px; right: 12px; margin: 0; }
		> [data-peel-beside="pull"] { right: calc(100% - 14px); }
	}
	@media (prefers-reduced-motion: reduce) {
		> [data-peel-card] { animation: none; transition: none; }
		&:has([data-peel-card-lift]) > [data-peel-card] { top: 0; }
	}
`;

/**
 * A row in the frame whose height opens and closes over the retreat (a fr-track transition). Tucking
 * closes the tag's row first and opens the pull's row only once the tuck finishes (owner 2026-09-10:
 * both animating at once made their heights sum past the settled height, and the height-capped
 * frame squashed the card for the length of the retreat). The tag overflows its closing track and
 * rides up behind the card meanwhile. Reduced motion transitions nothing: the slot tucks instantly.
 */
export const TagRow = styled.div<{$open: boolean}>`
	display: grid;
	grid-template-rows: ${({$open}) => $open ? "1fr" : "0fr"};
	transition: grid-template-rows ${TAG_RETREAT_MS}ms cubic-bezier(.4,0,.7,.3);
	/* The squashed item is a plain wrapper, never the tag: the retreat translates the tag by -100% of
	   ITS OWN height, and a tag squashed with the track ended up 26px up, in view below the card,
	   until it unmounted. The wrapper closes; the tag keeps its height and overflows it. */
	> * { min-height: 0; }
	@media (prefers-reduced-motion: reduce) { transition: none; }
`;

export const TagPosition = styled.div<{$pinned: boolean; $entrance: boolean; $ticket?: boolean; $leaving?: boolean; $beside?: boolean}>`
	position: relative;
	z-index: 1;
	width: min(${({$ticket}) => $ticket ? TICKET_WIDTH : GIFT_TAG_WIDTH}, 88%);
	margin: 14px 6% 0 auto;
	/* The ticket hangs centred under the card at every width and never widens past its cap: on the
	   stacked layouts (901px down) the card fills the viewport, and a 88%-wide ticket there read as
	   a banner with its print spread across it (owner 2026-09-09, at 730px and narrower). */
	${({$ticket}) => $ticket && css`margin-inline: auto;`}
	transform-origin: 50% -26px;
	${({$entrance, $pinned, $leaving}) => $entrance && !$pinned && !$leaving && css`
		animation: ${tagPendulum} 1.4s ease-out .25s both;
	`}
	/* The top gap closes with the row: a margin keeps its closed row 14px tall, so without this the
	   card moved those 14px at once when the tag unmounted. */
	transition: margin-top ${TAG_RETREAT_MS}ms cubic-bezier(.4,0,.7,.3);
	${({$leaving, $pinned}) => $leaving && !$pinned && css`
		animation: ${tagRetreat} ${TAG_RETREAT_MS}ms cubic-bezier(.3,.1,.3,1) both;
		margin-top: 0;
		pointer-events: none;
	`}
	${({$pinned, $ticket, theme}) => !$pinned && css`
		&::before {
			content: "";
			position: absolute;
			left: 50%; top: -28px; height: 38px;
			border-left: 1.5px solid ${$ticket ? theme.tokens.ticketRed : theme.tokens.goldEdge};
			pointer-events: none;
		}
	`}
	@media (max-width: 720px) { ${({$ticket}) => !$ticket && css`width: 88%;`} margin-inline: auto; }
	${({$beside, $ticket, $entrance, $leaving}) => $beside && css`
		&::before { content: none; }
		@media ${PEEL_WIDE_QUERY} {
			width: ${$ticket ? TICKET_WIDTH : GIFT_TAG_WIDTH};
			margin: 0;
			${$entrance && !$leaving && css`animation: ${besideSlideOut} .7s cubic-bezier(.2,.9,.3,1) .25s both;`}
			${$leaving && css`animation: ${besideSlideBack} ${TAG_RETREAT_MS}ms cubic-bezier(.3,.1,.3,1) both;`}
		}
		@media ${PEEL_COMPACT_QUERY} {
			width: min(${$ticket ? TICKET_WIDTH : "100%"}, 100%);
			margin: 0 auto;
			${$entrance && !$leaving && css`animation: ${overlayRise} .3s ease-out both;`}
			${$leaving && css`animation: ${overlayFade} ${TAG_RETREAT_MS}ms ease-in both;`}
		}
	`}
	${({$pinned}) => $pinned && css`
		position: absolute; top: -12px; right: -8px;
		z-index: 3; perspective: 900px;
		width: var(--peel-pin-width); margin: 0;
		transform: rotate(4deg); transform-origin: 50% 0;
		@media (max-width: 720px) {
			/* Natural height reserves room even when a long invite URL wraps on the back. */
			position: relative; top: auto; right: auto;
			align-self: flex-end; order: 0; margin: 12px 12px 8px;
			width: var(--peel-pin-width);
		}
	`}
	@media (prefers-reduced-motion: reduce) { animation: none; transition: none; }
`;

export const TagBody = styled.aside<{$ticket?: boolean}>`
	position: relative;
	text-align: left;
	${({$ticket}) => $ticket ? css`
		/* The tuck control inherits this: white, like the rest of the print on the red paper. */
		color: ${({theme}) => theme.tokens.darkButtonInk};
		/* The tuck control sits over the ticket's eyebrow band, so the print keeps clear of it here, and
		   only here: a ticket in a dialog has no tuck control over it (see PeelTicket.styles). */
		--ticket-tuck-clearance: 34px;
	` : css`
		border-radius: 14px;
		padding: 24px 16px 12px;
		${giftSurface}
		&::before {
			content: "";
			position: absolute; top: 9px; left: calc(50% - 3.5px);
			width: 7px; height: 7px; border-radius: 50%;
			background: ${({theme}) => theme.tokens.card};
			box-shadow: inset 0 0 0 1.5px ${({theme}) => theme.tokens.goldEdge};
		}
	`}
`;

export const Dismiss = styled.button<{$ticket?: boolean}>`
	position: absolute; top: 2px; right: 2px;
	${({$ticket}) => $ticket && css`left: 2px; right: auto;`}
	z-index: 1;
	width: 44px; height: 44px;
	display: grid; place-items: center;
	border: 0; border-radius: 12px;
	background: transparent; color: inherit;
	cursor: pointer;
	/* The ticket's × stays quiet on hover (owner 2026-09-09: a solid fill read as a block over the
	   coupon eyebrow): a light tint of the print colour, and the glyph keeps the print colour. */
	&:hover, &:focus-visible {
		background: ${({$ticket, theme}) => $ticket ? `color-mix(in srgb, ${theme.tokens.darkButtonInk} 18%, transparent)` : theme.tokens.goldInk};
		color: ${({theme}) => theme.tokens.darkButtonInk};
	}
	&:focus-visible { outline: 2px solid currentColor; outline-offset: -4px; }
`;
/* The pull's row grows during the retreat so the card slides; the pull itself waits until the tag
   has gone, then FALLS from behind the card (owner 2026-09-09), the tag's own drop in miniature. */
const pullDrop = keyframes`
	0% { transform: translateY(-100%); }
	70% { transform: translateY(2px); }
	100% { transform: translateY(0); }
`;
/* Beside the card the pull slides out from behind its left edge instead of falling. */
const pullSlide = keyframes`
	0% { transform: translateX(100%); }
	100% { transform: translateX(0); }
`;
export const Pulls = styled.div<{$hidden?: boolean; $falling?: boolean; $beside?: boolean}>`
	display: flex; justify-content: flex-end; gap: 8px;
	padding-inline: ${({$beside}) => $beside ? "0" : "6%"};
	${({$hidden}) => $hidden && css`visibility: hidden;`}
	${({$falling, $beside}) => $falling && css`> * { animation: ${$beside ? pullSlide : pullDrop} .45s cubic-bezier(.2,.9,.3,1) both; }`}
	@media (prefers-reduced-motion: reduce) { > * { animation: none; } }
`;
/* Gold is the invite's; the coupon's pull is its own red with white ink (owner 2026-09-09). */
export const Pull = styled.button<{$ticket?: boolean; $beside?: boolean}>`
	min-width: 46px; min-height: 44px;
	border: 1px solid ${({$ticket, theme}) => $ticket ? theme.tokens.ticketRedHover : theme.tokens.goldEdge};
	border-radius: 0 0 14px 14px;
	/* Beside the card the tab hangs from its left edge, so it is the bottom tab turned a quarter: rounded
	   on the outer side. Compact, it straddles the edge over the card's own padding, so it is narrower
	   (still well past the 24px minimum target) and fully rounded. */
	${({$beside}) => $beside && css`
		min-width: 44px; min-height: 48px; padding: 0;
		border-radius: 14px 0 0 14px;
		@media ${PEEL_COMPACT_QUERY} { min-width: 30px; border-radius: 12px; }
	`}
	background: ${({$ticket, theme}) => $ticket ? theme.tokens.ticketRed : theme.tokens.gold};
	color: ${({$ticket, theme}) => $ticket ? theme.tokens.darkButtonInk : theme.tokens.goldInk};
	cursor: pointer;
	&:focus-visible { outline: 2px solid ${({$ticket, theme}) => $ticket ? theme.tokens.ticketRed : theme.tokens.goldInk}; outline-offset: 2px; }
`;
/**
 * A tag in the dialog its tab opens (ui#3343, ui#3372): at the ticket's width, centred, never stretched
 * across a wider dialog. A gift tag's paper takes the same width, as it filled the card when it opened over
 * a phone's card.
 */
export const DialogTag = styled.div`
	width: min(${TICKET_WIDTH}, 100%);
	margin-inline: auto;
`;
export const Heading = styled.h3`
	margin: 0 28px 4px 0; font-size: 15px; font-weight: 700; line-height: 1.2;
`;
export const Content = styled.div`
	font-size: 14px; line-height: 1.45;
	color: ${({theme}) => theme.tokens.goldInk};
	p { margin: 0 0 8px; }
`;
export const BottomRow = styled.div`
	display: flex; align-items: center; justify-content: space-between; gap: 12px;
	margin-top: 8px;
`;
export const GiftGlyph = styled.span`
	display: grid; place-items: center; flex: none;
	width: 30px; height: 30px;
`;
export const TagAction = styled.button.attrs<{"data-peel-action"?: string}>({"data-peel-action": ""})`
	position: relative;
	font: inherit; font-size: 13px; font-weight: 700;
	text-decoration: none;
	min-height: 36px; padding: 7px 14px;
	border: 0; border-radius: 999px;
	background: ${({theme}) => theme.tokens.goldInk}; color: ${({theme}) => theme.tokens.darkButtonInk};
	cursor: pointer;
	/* Preserve the 44px hit area without making the pill visually taller. */
	&::before { content: ""; position: absolute; inset: -4px 0; }
	&:focus-visible { outline: 2px solid ${({theme}) => theme.tokens.goldInk}; outline-offset: 3px; }
`;
export const ConfettiCanvas = styled.canvas`
	position: absolute; inset: 0;
	width: 100%; height: 100%;
	pointer-events: none;
	@media (prefers-reduced-motion: reduce) { display: none; }
`;

export const CodeTurn = styled.aside<{$back: boolean}>`
	display: grid; position: relative;
	transform-style: preserve-3d;
	transform: rotateY(${({$back}) => $back ? 180 : 0}deg);
	transition: transform .7s cubic-bezier(.4,.1,.2,1);
	@media (prefers-reduced-motion: reduce) { transition: none; transform: none; }
`;
export const CodeFace = styled.div<{$reverse?: boolean}>`
	${giftSurface}
	grid-area: 1 / 1;
	display: grid; justify-items: center; align-content: center; gap: 8px;
	min-width: 0; padding: 18px 6px 12px;
	border-radius: 12px; text-align: center;
	backface-visibility: hidden;
	transform: rotateY(${({$reverse}) => $reverse ? 180 : 0}deg);
	&::before {
		content: ""; position: absolute; top: 5px; left: calc(50% - 3px);
		width: 6px; height: 6px; border-radius: 50%;
		background: ${({theme}) => theme.tokens.card};
		box-shadow: inset 0 0 0 1.5px ${({theme}) => theme.tokens.goldEdge};
	}
`;
export const CodeConfirmation = styled.h3`
	margin: 0; font-size: 14px; font-weight: 700; line-height: 1.2; color: inherit;
`;
export const CodeNumber = styled.code`
	font-family: ${fontFamilies.mono}; font-size: 14px; font-weight: 700; letter-spacing: .08em;
`;
export const CodeUrl = styled.small`
	font-size: 10px; line-height: 1.3; overflow-wrap: anywhere; min-width: 0;
`;
